/**
 * useAuth — single source of truth for the signed-in user across the app.
 *
 * Wraps `authApi` (which is a thin fetch layer) with:
 *   - tokenStorage persistence (handled by the api client interceptor)
 *   - a tiny external store so any component subscribing to `useAuth()`
 *     re-renders when login / logout / `me` resolves
 *   - cache primer on first mount: if an access token is already present
 *     (page reload), fetch `me` once and seed the user.
 *
 * The store is module-local. `AuthProvider` is intentionally NOT required;
 * the hook works standalone so the Login page can render before any other
 * provider mounts.
 */

import { useEffect, useSyncExternalStore } from "react";

import { authApi, type AuthUser, type TokenPair } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/errors";
import { tokenStorage } from "@/lib/api/tokens";

type Listener = () => void;

interface AuthState {
  user: AuthUser | null;
  status: "idle" | "loading" | "ready";
}

interface AuthStore {
  state: AuthState;
  listeners: Set<Listener>;
  primed: boolean;
}

/* Kept on `globalThis` so a dev hot reload of this module reuses the live
   store. A fresh copy would sit at "loading" forever while the check in
   flight resolved into the old one, holding the launch screen. */
const holder = globalThis as typeof globalThis & { __drtutorAuth?: AuthStore };
const store: AuthStore = (holder.__drtutorAuth ??= {
  state: { user: null, status: "idle" },
  listeners: new Set<Listener>(),
  primed: false,
});

/** The launch screen never waits longer than this for the sign-in check. */
const PRIME_TIMEOUT_MS = 12_000;

function notify(): void {
  for (const l of store.listeners) l();
}

function setState(next: Partial<AuthState>): void {
  store.state = { ...store.state, ...next };
  notify();
}

function subscribe(l: Listener): () => void {
  store.listeners.add(l);
  return () => {
    store.listeners.delete(l);
  };
}

function getSnapshot(): AuthState {
  return store.state;
}

async function primeFromToken(): Promise<void> {
  if (store.primed) return;
  store.primed = true;
  if (!tokenStorage.getAccess()) {
    setState({ status: "ready" });
    return;
  }
  setState({ status: "loading" });
  let timer: number | undefined;
  try {
    const timeout = new Promise<never>((_, reject) => {
      timer = window.setTimeout(() => reject(new Error("timeout")), PRIME_TIMEOUT_MS);
    });
    const user = await Promise.race([authApi.me(), timeout]);
    setState({ user, status: "ready" });
  } catch (err) {
    // A refused session is cleared. A slow or unreachable server keeps the
    // tokens, so the next load can still pick the session up.
    if (err instanceof ApiError) tokenStorage.clear();
    setState({ user: null, status: "ready" });
  } finally {
    window.clearTimeout(timer);
  }
}

/** Whether the signed-in person owns the institute. For code outside React (toasts). */
export function currentUserIsOwner(): boolean {
  return store.state.user?.isOwner === true;
}

export interface UseAuth {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isReady: boolean;
  login: (identifier: string, password: string) => Promise<AuthUser>;
  /**
   * Adopt a session from a token pair we already hold, without a second
   * round trip. Used by /activate, where accepting the invite already
   * returned tokens.
   */
  adoptSession: (pair: TokenPair) => AuthUser;
  logout: () => Promise<void>;
  refresh: () => Promise<AuthUser | null>;
}

export function useAuth(): UseAuth {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    void primeFromToken();
  }, []);

  return {
    user: snap.user,
    isAuthenticated: snap.user !== null,
    isReady: snap.status === "ready",
    async login(identifier, password) {
      const pair = await authApi.login(identifier, password);
      tokenStorage.set({ access: pair.access, refresh: pair.refresh });
      setState({ user: pair.user, status: "ready" });
      return pair.user;
    },
    adoptSession(pair) {
      tokenStorage.set({ access: pair.access, refresh: pair.refresh });
      setState({ user: pair.user, status: "ready" });
      return pair.user;
    },
    async logout() {
      const refresh = tokenStorage.getRefresh();
      try {
        await authApi.logout(refresh);
      } catch {
        // Even if the server refuses (revoked / expired), wipe locally.
      }
      tokenStorage.clear();
      setState({ user: null, status: "ready" });
    },
    async refresh() {
      if (!tokenStorage.getAccess()) {
        setState({ user: null, status: "ready" });
        return null;
      }
      try {
        const user = await authApi.me();
        setState({ user, status: "ready" });
        return user;
      } catch {
        tokenStorage.clear();
        setState({ user: null, status: "ready" });
        return null;
      }
    },
  };
}
